import { CORES_DE_TAG, NOME_DO_TIPO, type TipoDeTag } from '@/features/tags/dados/tags';
import type { Esquema } from '@/lib/api/tipos';
import { formatarDataHora } from '@/lib/datas';

export type AcaoDeAuditoria = Esquema<'AcaoDeAuditoria'>;

/**
 * Texto de cada ação do backend (spec 016, CA-01). O `satisfies` obriga a traduzir todas as do
 * OpenAPI: uma ação nova no backend quebra o typecheck depois do `pnpm api:tipos`.
 */
export const TEXTO_DA_ACAO = {
  'sessao.login': 'Entrou no sistema',
  'sessao.login_falhou': 'Tentativa de login falhou',
  'sessao.logout': 'Saiu do sistema',
  'sessao.encerradas': 'Encerrou sessões',
  'senha.trocada': 'Trocou a senha',
  'senha.redefinida': 'Redefiniu a senha',
  'dois_fatores.ativado': 'Ativou a verificação em duas etapas',
  'dois_fatores.desativado': 'Desativou a verificação em duas etapas',
  'super_admin.convidado': 'Convidou para administrar a plataforma',
  'super_admin.convite_reenviado': 'Reenviou o convite de administração da plataforma',
  'empresa.criada': 'Criou a academia',
  'empresa.convite_reenviado': 'Reenviou o convite do dono',
  'empresa.modulos_alterados': 'Ligou ou desligou módulos da academia',
  'tag.criada': 'Criou uma tag',
  'tag.editada': 'Editou uma tag',
  'tag.excluida': 'Excluiu uma tag',
  'empresa.editada': 'Editou os dados da academia',
  'cores.trocadas': 'Trocou as cores do painel',
  'cadastro.criado': 'Criou',
  'cadastro.editado': 'Editou',
  'cadastro.excluido': 'Excluiu',
  'funcao.telas_trocadas': 'Trocou as telas da função',
  'acessos.criados': 'Liberou acessos',
  'acessos.excluidos': 'Retirou acessos',
  'usuario.criado': 'Cadastrou uma pessoa',
  'usuario.editado': 'Editou uma pessoa',
  'usuario.funcao_trocada': 'Trocou a função de uma pessoa',
  'usuario.perfil_editado': 'Editou o perfil do aluno',
  'usuario.convite_reenviado': 'Reenviou o convite',
  'usuario.removido': 'Removeu uma pessoa da academia',
  'ficha.item_criado': 'Incluiu exercício na ficha',
  'ficha.item_editado': 'Editou exercício da ficha',
  'ficha.itens_reordenados': 'Reordenou os exercícios da ficha',
  'ficha.item_removido': 'Tirou exercício da ficha',
  'ficha.duplicada': 'Duplicou a ficha',
  'plano.duplicado': 'Duplicou o plano',
  'plano.ficha_vinculada': 'Incluiu ficha no plano',
  'plano.ficha_reordenada': 'Mudou a ordem de uma ficha do plano',
  'plano.fichas_reordenadas': 'Reordenou as fichas do plano',
  'plano.ficha_desvinculada': 'Tirou ficha do plano',
  'plano.ficha_personalizada': 'Personalizou a ficha do plano',
  'plano.removido_de_vez': 'Removeu o plano de vez',
  'treino.registro_excluido': 'Excluiu um treino realizado',
  'atendimento.criado': 'Criou uma prescrição',
  'atendimento.editado': 'Editou uma prescrição',
  'atendimento.excluido': 'Excluiu uma prescrição',
  'pagamento.cliente_criado': 'Cadastrou cliente de pagamento',
  'pagamento.cliente_atualizado': 'Atualizou cliente de pagamento',
  'pagamento.assinatura_criada': 'Criou uma assinatura',
  'pagamento.assinatura_atualizada': 'Alterou uma assinatura',
  'pagamento.assinatura_cancelada': 'Cancelou uma assinatura',
  'pagamento.link_criado': 'Gerou link de pagamento',
  'pagamento.link_cancelado': 'Cancelou link de pagamento',
  'pagamento.assinatura_criada_por_link': 'Assinatura paga pelo link',
  'pagamento.status_alterado': 'Situação da assinatura mudou',
  'documento.criado': 'Publicou um documento',
  'documento.editado': 'Editou um documento',
  'documento.arquivo_trocado': 'Trocou o arquivo do documento',
  'documento.excluido': 'Excluiu um documento',
  'documento.aluno_vinculado': 'Vinculou um aluno ao documento',
  'documento.aluno_desvinculado': 'Desvinculou um aluno do documento',
  'curso.criado': 'Criou um curso',
  'curso.editado': 'Editou um curso',
  'curso.situacao_trocada': 'Mudou a situação do curso',
  'curso.capa_trocada': 'Trocou a capa do curso',
  'curso.excluido': 'Excluiu um curso',
  'curso.aluno_vinculado': 'Vinculou um aluno ao curso',
  'curso.aluno_desvinculado': 'Desvinculou um aluno do curso',
  'curso.modulo_criado': 'Criou um módulo do curso',
  'curso.modulo_editado': 'Renomeou um módulo do curso',
  'curso.modulo_excluido': 'Excluiu um módulo do curso',
  'curso.modulos_reordenados': 'Reordenou os módulos do curso',
  'curso.aula_criada': 'Criou uma aula',
  'curso.aula_editada': 'Editou uma aula',
  'curso.aula_excluida': 'Excluiu uma aula',
  'curso.aulas_reordenadas': 'Reordenou as aulas do módulo',
  'curso.material_trocado': 'Trocou o material de apoio da aula',
  'banner.criado': 'Criou um banner do app',
  'banner.editado': 'Editou um banner do app',
  'banner.imagem_trocada': 'Trocou a imagem de um banner do app',
  'banner.excluido': 'Excluiu um banner do app',
  'banner.reordenados': 'Reordenou os banners do app',
  'pagamento.compra_criada_por_link': 'Documento comprado pelo link',
  'pagamento.compra_status_alterado': 'Situação da compra mudou',
} as const satisfies Record<AcaoDeAuditoria, string>;

export const ACOES = Object.keys(TEXTO_DA_ACAO) as AcaoDeAuditoria[];

/** Nome de cada `recurso` gravado pelo backend; um desconhecido aparece como veio. */
const NOME_DO_RECURSO: Record<string, string> = {
  acessos: 'Acessos',
  assinaturas: 'Assinatura',
  banners: 'Banner do app',
  atendimentos: 'Prescrição',
  'categorias-ficha': 'Categoria de ficha',
  clientes: 'Cliente de pagamento',
  'compras-documento': 'Compra de documento',
  cores: 'Cor',
  dificuldades: 'Dificuldade',
  documentos: 'Documento',
  cursos: 'Curso',
  empresa: 'Academia',
  equipamentos: 'Equipamento',
  exercicios: 'Exercício',
  fichas: 'Ficha',
  funcoes: 'Função',
  'grupos-musculares': 'Grupo muscular',
  'links-pagamento': 'Link de pagamento',
  objetivos: 'Objetivo',
  planos: 'Plano',
  'registros-treino': 'Treino realizado',
  'status-usuario': 'Status',
  telas: 'Tela',
  'tipos-atendimento': 'Tipo de prescrição',
  tags: 'Tag',
  unidades: 'Unidade',
  usuario: 'Pessoa',
};

export function nomeDoRecurso(recurso: string): string {
  return NOME_DO_RECURSO[recurso] ?? recurso;
}

/** "Unidade · KG", "Ficha · Treino A", "Pessoa" ou "—" (CA-01). */
export function alvoDoEvento(evento: {
  recurso: string | null;
  dados: Record<string, unknown>;
}): string {
  const rotulo = typeof evento.dados.rotulo === 'string' ? evento.dados.rotulo : undefined;
  if (evento.recurso === null) return rotulo ?? '—';
  const nome = nomeDoRecurso(evento.recurso);
  return rotulo === undefined ? nome : `${nome} · ${rotulo}`;
}

/** Nome dos campos de `dados` no detalhe (CA-02); os outros aparecem como vieram. */
const NOME_DO_DADO: Record<string, string> = {
  rotulo: 'Alvo',
  campos: 'Campos alterados',
  motivo: 'Motivo',
  email: 'Email informado',
  doisFatores: 'Com verificação em duas etapas',
  convite: 'Primeira senha (convite)',
  papel: 'Papel',
  papelAnterior: 'Papel anterior',
  papelNovo: 'Papel novo',
  funcao: 'Função',
  semOutroVinculo: 'Ficou sem outra academia',
  descricao: 'Descrição',
  valorCentavos: 'Valor',
  status: 'Situação',
  statusAnterior: 'Situação anterior',
  tipo: 'Evento da Pagar.me',
  ficha: 'Ficha',
  exercicio: 'Exercício',
  ordem: 'Posição',
  incluidas: 'Telas incluídas',
  removidas: 'Telas retiradas',
  cores: 'Cores',
  dono: 'Dono',
  ligados: 'Módulos ligados',
  desligados: 'Módulos desligados',
  cadastro: 'Cadastro',
  cor: 'Cor',
  usos: 'Registros que usavam',
  contaCriada: 'Conta criada agora',
  copiouFichas: 'Copiou as fichas',
  realizadoEm: 'Realizado em',
  rota: 'Como',
};

export function nomeDoDado(chave: string): string {
  return NOME_DO_DADO[chave] ?? chave;
}

const reais = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const QUANDO = new Set(['realizadoEm']);

/** Valor de um campo de `dados` para leitura: listas, sim/não, dinheiro e datas (CA-02). */
export function textoDoDado(chave: string, valor: unknown): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
  if (chave === 'valorCentavos' && typeof valor === 'number') return reais.format(valor / 100);
  if (QUANDO.has(chave) && typeof valor === 'string') return formatarDataHora(valor);
  if (Array.isArray(valor)) return valor.length === 0 ? '—' : valor.map(String).join(', ');
  if (chave === 'cadastro' && typeof valor === 'string' && valor in NOME_DO_TIPO) {
    return NOME_DO_TIPO[valor as TipoDeTag];
  }
  if (chave === 'cor' && typeof valor === 'string') {
    return CORES_DE_TAG.find((c) => c.cor === valor)?.nome ?? valor;
  }
  if (typeof valor === 'string') return valor;
  if (typeof valor === 'number') return valor.toLocaleString('pt-BR');
  return JSON.stringify(valor);
}

/** Quem fez, quando não há pessoa na sessão (CA-01). */
export function autorSemPessoa(acao: AcaoDeAuditoria): string {
  if (acao === 'pagamento.status_alterado') return 'Pagar.me';
  if (acao === 'pagamento.assinatura_criada_por_link') return 'Aluno, pelo link';
  if (acao === 'sessao.login_falhou') return 'Conta desconhecida';
  return 'Sistema';
}
